import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { LegalLayout, type LegalTocItem } from "@/components/legal/LegalLayout";
import { LegalText } from "@/components/legal/LegalText";
import { termsTranslations } from "@/lib/legal/terms";
import { isLandingLocale } from "@/lib/landing/locale";

export default function Terms() {
  const { lang } = useParams<{ lang: string }>();
  // The language comes from the URL (as on the landing page), so the
  // prerendered HTML and the first client render agree.
  const locale = isLandingLocale(lang) ? lang : "cs";
  const t = termsTranslations[locale];
  const toc: LegalTocItem[] = t.articles.map((article, a) => ({
    id: `cl-${a + 1}`,
    label: `${a + 1}. ${article.title}`,
  }));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalLayout
      locale={locale}
      path="/terms"
      title={t.title}
      subtitle={t.subtitle}
      effective={t.heroPill}
      toc={toc}
    >
      {t.bindingNote && (
        <p className="mb-10 rounded-lg border bg-secondary/40 px-4 py-3 text-sm text-muted-foreground">
          {t.bindingNote}
        </p>
      )}

      <div className="space-y-10">
        {t.articles.map((article, a) => {
          const num = a + 1;
          return (
            <section key={article.title} id={`cl-${num}`} className="scroll-mt-24">
              {a > 0 && <hr className="mb-10 border-border/70" />}
              <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
                {num}. {article.title}
              </h2>
              <ol className="space-y-3">
                {article.clauses.map((clause, c) => (
                  <li key={c} className="flex gap-3">
                    <span className="w-9 shrink-0 tabular-nums text-muted-foreground/70">
                      {num}.{c + 1}
                    </span>
                    <div className="min-w-0">
                      <p>
                        <LegalText text={clause.text} locale={locale} />
                      </p>
                      {clause.items && (
                        <ul className="mt-2 list-disc space-y-1.5 pl-5 marker:text-[hsl(var(--action))] dark:marker:text-primary">
                          {clause.items.map((item) => (
                            <li key={item}>
                              <LegalText text={item} locale={locale} />
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          );
        })}

        <div className="border-t pt-8 text-sm text-muted-foreground">
          <p>
            {t.contactLabel}{" "}
            <a
              href="mailto:info@idex-corp.com"
              className="font-medium text-[hsl(var(--action))] underline-offset-2 hover:underline dark:text-primary"
            >
              info@idex-corp.com
            </a>
          </p>
          <p className="mt-3">{t.closingPlaceDate}</p>
          <p className="mt-1 font-medium text-foreground">IDEx Corporate s.r.o.</p>
        </div>
      </div>
    </LegalLayout>
  );
}
